/**
 * Behind auth. Everything lazy through the route object's own `lazy`, so a chunk loads with the
 * router's pending state rather than blanking the screen behind a Suspense fallback.
 *
 * The permissions here are the same constants `navItems.ts` uses, which is what stops a nav item
 * and its route disagreeing about who may open a screen.
 */

import type { RouteObject } from 'react-router';

import { Permission } from '@pos/shared';

export const appRoutes: RouteObject[] = [
  // Landing depends on the role: an owner wants every sale, a salesperson wants their own. Both
  // are permission-gated, so whichever they lack redirects past it.
  { index: true, lazy: async () => ({ Component: (await import('@/pages/LandingRedirect')).LandingRedirect }) },

  {
    path: 'home',
    lazy: async () => ({ Component: (await import('@/pages/HomePage')).HomePage }),
    handle: { title: 'Home' },
  },

  {
    path: 'sales',
    lazy: async () => ({ Component: (await import('@/pages/SalesPage')).SalesPage }),
    handle: { title: 'Sales', permission: Permission.SaleViewAll },
  },
  {
    path: 'sales/:saleId',
    lazy: async () => ({ Component: (await import('@/pages/SaleDetailPage')).SaleDetailPage }),
    handle: { title: 'Sale', permission: Permission.SaleViewAll },
  },
  // ── Money ─────────────────────────────────────────────────────────────────
  {
    path: 'payments',
    lazy: async () => ({ Component: (await import('@/pages/PaymentsPage')).PaymentsPage }),
    handle: { title: 'Payments', permission: Permission.PaymentView },
  },
  {
    path: 'payments/unmatched',
    lazy: async () => ({ Component: (await import('@/pages/UnmatchedPage')).UnmatchedPage }),
    handle: { title: 'Unmatched payments', permission: Permission.PaymentUnmatchedView },
  },
  {
    path: 'reconciliation',
    lazy: async () => ({ Component: (await import('@/pages/ReconciliationPage')).ReconciliationPage }),
    handle: { title: 'Reconciliation', permission: Permission.PaymentReconcileView },
  },
  {
    path: 'cash',
    lazy: async () => ({ Component: (await import('@/pages/CashPage')).CashPage }),
    handle: { title: 'Cash', permission: Permission.CashViewPosition },
  },

  {
    path: 'me/sales',
    lazy: async () => ({ Component: (await import('@/pages/MySalesPage')).MySalesPage }),
    handle: { title: 'My sales', permission: Permission.SaleViewOwn },
  },
  {
    path: 'me/sales/:saleId',
    lazy: async () => ({ Component: (await import('@/pages/SaleDetailPage')).SaleDetailPage }),
    handle: { title: 'Sale', permission: Permission.SaleViewOwn },
  },
  {
    path: 'profile',
    lazy: async () => ({ Component: (await import('@/pages/ProfilePage')).ProfilePage }),
    handle: { title: 'Profile' },
  },
  {
    path: 'help',
    lazy: async () => ({ Component: (await import('@/pages/HelpPage')).HelpPage }),
    handle: { title: 'Help' },
  },

  // ── Setup ─────────────────────────────────────────────────────────────────
  // Phase 1 scope, not Phase 4: revoking a device is an auth/security action, not the deferred
  // user-management admin screen (create, edit, role assignment, deactivation UI). Those wait.
  {
    path: 'users',
    lazy: async () => ({ Component: (await import('@/pages/UsersPage')).UsersPage }),
    handle: { title: 'Users', permission: Permission.UserView },
  },
  {
    path: 'users/:userId',
    lazy: async () => ({ Component: (await import('@/pages/UserDetailPage')).UserDetailPage }),
    handle: { title: 'User', permission: Permission.UserView },
  },
  {
    path: 'roles',
    lazy: async () => ({ Component: (await import('@/pages/RolesPage')).RolesPage }),
    handle: { title: 'Roles', permission: Permission.RoleView },
  },
  {
    path: 'roles/:roleId',
    lazy: async () => ({ Component: (await import('@/pages/RoleDetailPage')).RoleDetailPage }),
    handle: { title: 'Role', permission: Permission.RoleView },
  },
  {
    path: 'shops',
    lazy: async () => ({ Component: (await import('@/pages/ShopsPage')).ShopsPage }),
    handle: { title: 'Shops', permission: Permission.ShopView },
  },
  {
    path: 'categories',
    lazy: async () => ({ Component: (await import('@/pages/CategoriesPage')).CategoriesPage }),
    handle: { title: 'Categories', permission: Permission.CategoryView },
  },
  {
    path: 'products',
    lazy: async () => ({ Component: (await import('@/pages/ProductsPage')).ProductsPage }),
    handle: { title: 'Products', permission: Permission.ProductView },
  },
  {
    // Before products/:productId, so "new" is never read as an id.
    path: 'products/new',
    lazy: async () => ({ Component: (await import('@/pages/NewProductPage')).NewProductPage }),
    handle: { title: 'New product', permission: Permission.ProductCreate },
  },
  {
    path: 'products/:productId',
    lazy: async () => ({ Component: (await import('@/pages/ProductDetailPage')).ProductDetailPage }),
    handle: { title: 'Product', permission: Permission.ProductView },
  },
  {
    path: 'variants',
    lazy: async () => ({ Component: (await import('@/pages/VariantsPage')).VariantsPage }),
    handle: { title: 'Items', permission: Permission.ProductView },
  },
  {
    path: 'variants/:variantId',
    lazy: async () => ({ Component: (await import('@/pages/VariantDetailPage')).VariantDetailPage }),
    handle: { title: 'Item', permission: Permission.ProductView },
  },
  {
    path: 'units/lookup',
    lazy: async () => ({ Component: (await import('@/pages/SerialLookupPage')).SerialLookupPage }),
    handle: { title: 'Find a unit', permission: [Permission.UnitView, Permission.CostView] },
  },
  {
    path: 'suppliers',
    lazy: async () => ({ Component: (await import('@/pages/SuppliersPage')).SuppliersPage }),
    handle: { title: 'Suppliers', permission: Permission.SupplierView },
  },
  {
    path: 'purchases',
    lazy: async () => ({ Component: (await import('@/pages/PurchasesPage')).PurchasesPage }),
    handle: { title: 'Purchases', permission: [Permission.PurchaseView, Permission.CostView] },
  },
  {
    path: 'purchases/new',
    lazy: async () => ({ Component: (await import('@/pages/PurchaseNewPage')).PurchaseNewPage }),
    handle: { title: 'New purchase', permission: [Permission.PurchaseCreate, Permission.CostView] },
  },
  {
    path: 'purchases/:purchaseId',
    lazy: async () => ({ Component: (await import('@/pages/PurchaseDetailPage')).PurchaseDetailPage }),
    handle: { title: 'Purchase', permission: [Permission.PurchaseView, Permission.CostView] },
  },
  {
    path: 'stock',
    lazy: async () => ({ Component: (await import('@/pages/StockPage')).StockPage }),
    handle: { title: 'Stock', permission: [Permission.StockView, Permission.CostView] },
  },
  {
    path: 'stock/lookup',
    lazy: async () => ({ Component: (await import('@/pages/StockLookupPage')).StockLookupPage }),
    handle: { title: 'Find stock', permission: Permission.StockView },
  },
  {
    path: 'imports',
    lazy: async () => ({ Component: (await import('@/pages/ImportsPage')).ImportsPage }),
    handle: { title: 'Imports', permission: Permission.ImportDryRun },
  },
  {
    path: 'audit',
    lazy: async () => ({ Component: (await import('@/pages/AuditPage')).AuditPage }),
    handle: { title: 'Audit trail', permission: Permission.AuditView },
  },

  // ── Phase 4: stock integrity ──────────────────────────────────────────────
  // Each family is gated on its *view* permission, matching the nav. The narrower permissions
  // (dispatch, receive, approve, resolve) gate controls inside the screen, not the route: a
  // manager who may receive but not approve a loss must still be able to open the transfer.
  {
    path: 'transfers',
    lazy: async () => ({ Component: (await import('@/pages/TransfersPage')).TransfersPage }),
    handle: { title: 'Transfers', permission: Permission.TransferView },
  },
  // Before `transfers/:transferId`, so "new" is never parsed as an id.
  {
    path: 'transfers/new',
    lazy: async () => ({ Component: (await import('@/pages/TransferNewPage')).TransferNewPage }),
    handle: { title: 'New transfer', permission: Permission.TransferCreate },
  },
  {
    path: 'transfers/:transferId',
    lazy: async () => ({ Component: (await import('@/pages/TransferDetailPage')).TransferDetailPage }),
    handle: { title: 'Transfer', permission: Permission.TransferView },
  },
  {
    path: 'stock-counts',
    lazy: async () => ({ Component: (await import('@/pages/StockCountsPage')).StockCountsPage }),
    handle: { title: 'Stock counts', permission: Permission.StockCountView },
  },
  {
    path: 'stock-counts/:countId',
    lazy: async () => ({ Component: (await import('@/pages/StockCountDetailPage')).StockCountDetailPage }),
    handle: { title: 'Stock count', permission: Permission.StockCountView },
  },
  {
    path: 'faulty-stock',
    lazy: async () => ({ Component: (await import('@/pages/FaultyStockPage')).FaultyStockPage }),
    handle: { title: 'Faulty stock', permission: [Permission.WriteOffView, Permission.CostView] },
  },
  {
    path: 'write-offs',
    lazy: async () => ({ Component: (await import('@/pages/WriteOffsPage')).WriteOffsPage }),
    handle: { title: 'Write-offs', permission: [Permission.WriteOffView, Permission.CostView] },
  },
  {
    path: 'settlements',
    lazy: async () => ({ Component: (await import('@/pages/SettlementsPage')).SettlementsPage }),
    handle: { title: 'Settlements', permission: Permission.SettlementView },
  },
  {
    path: 'claims',
    lazy: async () => ({ Component: (await import('@/pages/ClaimsPage')).ClaimsPage }),
    handle: { title: 'Supplier claims', permission: [Permission.ClaimView, Permission.CostView] },
  },
  // ── The online store ─────────────────────────────────────────────────────
  {
    path: 'online/orders',
    lazy: async () => ({ Component: (await import('@/pages/OnlineOrdersPage')).OnlineOrdersPage }),
    handle: { title: 'Online orders', permission: Permission.OnlineOrderView },
  },
  {
    path: 'online/orders/:orderId',
    lazy: async () => ({ Component: (await import('@/pages/OnlineOrderPage')).OnlineOrderPage }),
    handle: { title: 'Online order', permission: Permission.OnlineOrderView },
  },
  {
    path: 'online/listings',
    lazy: async () => ({ Component: (await import('@/pages/OnlineListingsPage')).OnlineListingsPage }),
    handle: { title: 'Online listings', permission: Permission.OnlineView },
  },
  {
    path: 'online/listings/:listingId',
    lazy: async () => ({ Component: (await import('@/pages/OnlineListingPage')).OnlineListingPage }),
    handle: { title: 'Online listing', permission: Permission.OnlineView },
  },
  {
    path: 'online/settings',
    lazy: async () => ({ Component: (await import('@/pages/StorefrontSettingsPage')).StorefrontSettingsPage }),
    handle: { title: 'Storefront', permission: Permission.OnlineStorefrontManage },
  },
  {
    path: 'prices/bulk',
    lazy: async () => ({ Component: (await import('@/pages/BulkPricesPage')).BulkPricesPage }),
    handle: { title: 'Bulk prices', permission: Permission.PriceBulkExport },
  },

  // ── Phase 5: oversight ─────────────────────────────────────────────────────
  {
    path: 'review',
    lazy: async () => ({ Component: (await import('@/pages/ReviewQueuePage')).ReviewQueuePage }),
    handle: { title: 'Review queue', permission: Permission.ReviewView },
  },
  {
    path: 'disputes',
    lazy: async () => ({ Component: (await import('@/pages/DisputesPage')).DisputesPage }),
    handle: { title: 'Disputes', permission: Permission.DisputeCreate },
  },
  {
    path: 'approvals',
    lazy: async () => ({ Component: (await import('@/pages/ApprovalsPage')).ApprovalsPage }),
    handle: { title: 'Approvals', permission: Permission.ApprovalDecide },
  },
  {
    path: 'brokers',
    lazy: async () => ({ Component: (await import('@/pages/BrokersPage')).BrokersPage }),
    handle: { title: 'Brokers', permission: Permission.BrokerView },
  },
  {
    path: 'customers',
    lazy: async () => ({ Component: (await import('@/pages/CustomersPage')).CustomersPage }),
    handle: { title: 'Customers', permission: Permission.CustomerView },
  },
  {
    path: 'reports',
    lazy: async () => ({ Component: (await import('@/pages/ReportsPage')).ReportsPage }),
    handle: { title: 'Reports', permission: Permission.ReportView },
  },
  {
    // Deep link to one report. This is what makes drill-through real: the dashboard links here
    // with `?from`, `?to` and `?shopId` already on the URL, so the report opens on the same
    // question the figure answered — and the link is something a person can send to somebody.
    path: 'reports/:reportId',
    lazy: async () => ({ Component: (await import('@/pages/ReportsPage')).ReportsPage }),
    handle: { title: 'Reports', permission: Permission.ReportView },
  },

  // ── Phase 6: printing and notifications ───────────────────────────────────
  {
    path: 'print-agents',
    lazy: async () => ({ Component: (await import('@/pages/PrintAgentsPage')).PrintAgentsPage }),
    handle: { title: 'Print agents', permission: Permission.PrinterManage },
  },
  {
    path: 'print-jobs',
    lazy: async () => ({ Component: (await import('@/pages/PrintJobsPage')).PrintJobsPage }),
    handle: { title: 'Print jobs', permission: Permission.PrintJobView },
  },
  {
    path: 'notifications',
    lazy: async () => ({ Component: (await import('@/pages/NotificationsPage')).NotificationsPage }),
    handle: { title: 'Notifications', permission: Permission.NotificationManage },
  },
];
